import { convertFieldValueAvroToJson } from 'rpc-client-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetListingDetailsV3Query } from '../../../__generated__/chirp/com/hubspot/marketplace/listing/details/rpc/MarketplaceListingDetailsRpc-app.hooks';
import { useGetInstallability } from './useGetInstallability';
import { jsx as _jsx } from "react/jsx-runtime";
function AdditionalListingInstallabilityCollector({
  listingId,
  published,
  appContext,
  productType,
  onResult
}) {
  const {
    loading,
    portalInstallable,
    userInstallable,
    missingPlanTooltip,
    isOnlyMissingPublicBetaAccessForInstall,
    refetchInstallability
  } = useGetInstallability({
    listingId,
    published,
    appContext,
    productType
  });
  const {
    data: listingDetailsData,
    loading: listingDetailsLoading
  } = useGetListingDetailsV3Query({
    variables: {
      listingId
    },
    skip: !isOnlyMissingPublicBetaAccessForInstall
  });
  const betaInfo = useMemo(() => {
    if (!isOnlyMissingPublicBetaAccessForInstall || listingDetailsLoading) {
      return undefined;
    }
    const parsed = listingDetailsData !== null && listingDetailsData !== void 0 && listingDetailsData.listing ? convertFieldValueAvroToJson(listingDetailsData.listing) : {};
    if (!parsed.name || !parsed.urlSlug || !parsed.betaDetails) {
      return undefined;
    }
    return {
      name: parsed.name,
      slug: parsed.urlSlug,
      betaDetails: parsed.betaDetails,
      refetchInstallability: () => void refetchInstallability()
    };
  }, [isOnlyMissingPublicBetaAccessForInstall, listingDetailsLoading, listingDetailsData, refetchInstallability]);
  const combinedLoading = loading || isOnlyMissingPublicBetaAccessForInstall && listingDetailsLoading;
  const missingPlanTooltipRef = useRef(missingPlanTooltip);
  // eslint-disable-next-line react-hooks/refs
  missingPlanTooltipRef.current = missingPlanTooltip;
  useEffect(() => {
    onResult(listingId, {
      loading: combinedLoading,
      portalInstallable,
      userInstallable,
      missingPlanTooltip: missingPlanTooltipRef.current,
      betaInfo
    });
  }, [listingId, combinedLoading, portalInstallable, userInstallable, onResult, betaInfo]);
  return null;
}
export function useAdditionalListingInstallability({
  requiredForInstallListingIds,
  published,
  appContext,
  productType,
  primaryLoading,
  primaryPortalInstallable,
  primaryUserInstallable,
  primaryMissingPlanTooltip
}) {
  var _additionalResultValu;
  const [additionalResults, setAdditionalResults] = useState({});
  const handleResult = useCallback((id, result) => {
    setAdditionalResults(prev => Object.assign({}, prev, {
      [id]: result
    }));
  }, []);
  const requiredIds = requiredForInstallListingIds !== null && requiredForInstallListingIds !== void 0 ? requiredForInstallListingIds : [];
  const additionalResultValues = Object.values(additionalResults);
  const allAdditionalResultsLoaded = requiredIds.length === 0 || requiredIds.every(id => id in additionalResults);
  const collectors = requiredIds.map(id => /*#__PURE__*/_jsx(AdditionalListingInstallabilityCollector, {
    listingId: id,
    published: published,
    appContext: appContext,
    productType: productType,
    onResult: handleResult
  }, id));
  const requiredPublicBetaListings = additionalResultValues.flatMap(r => r.betaInfo ? [r.betaInfo] : []);
  return {
    collectors,
    effectiveLoading: primaryLoading || !allAdditionalResultsLoaded || additionalResultValues.some(r => r.loading),
    effectivePortalInstallable: primaryPortalInstallable && additionalResultValues.every(r => r.portalInstallable),
    effectiveUserInstallable: primaryUserInstallable && additionalResultValues.every(r => r.userInstallable),
    effectiveMissingPlanTooltip: primaryMissingPlanTooltip !== null && primaryMissingPlanTooltip !== void 0 ? primaryMissingPlanTooltip : (_additionalResultValu = additionalResultValues.find(r => r.missingPlanTooltip)) === null || _additionalResultValu === void 0 ? void 0 : _additionalResultValu.missingPlanTooltip,
    requiredPublicBetaListings
  };
}